import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismModal,
  PrismTable,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type {
  DateRange,
  PeriodWithPropertiesWithIncompleteData,
  PropertyWithIncompleteData,
} from 'lumen/components/market/total-revenue/elegant-date-handling';

const COLUMN_PROPERTIES = {
  NAME: 'name',
  DATES_WITHOUT_DATA: 'datesWithoutData',
};

interface MarketTotalRevenueElegantDateHandlingModalSignature {
  Args: {
    canRemovePropertiesFromFilter: boolean;
    onClose: () => void;
    onRemovePropertiesFromFilter: (propertyIds: string[]) => void;
    periodsWithPropertiesWithIncompleteData: PeriodWithPropertiesWithIncompleteData[];
  };
  Blocks: {
    default: [];
  };
}

export default class MarketTotalRevenueElegantDateHandlingModal extends Component<MarketTotalRevenueElegantDateHandlingModalSignature> {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    return [
      {
        name: this.intl.t('general.hotelname'),
        textAlign: 'left' as const,
        valuePath: COLUMN_PROPERTIES.NAME,
      },
      {
        name: this.intl.t('total_revenue.dates_without_data'),
        textAlign: 'left' as const,
        valuePath: COLUMN_PROPERTIES.DATES_WITHOUT_DATA,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRemoveFiltersDisabled() {
    return !this.args.canRemovePropertiesFromFilter;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get periodsWithRows() {
    return this.args.periodsWithPropertiesWithIncompleteData.map(
      (referencePeriod) => ({
        title: referencePeriod.periodTranslation,
        rows: this.getRowsForPropertiesWithIncompleteData(
          referencePeriod.properties,
        ),
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatDateRanges(periodsWithoutData: DateRange[]) {
    return periodsWithoutData
      .map(
        (period) => `${period.from.format('ll')} - ${period.to.format('ll')}`,
      )
      .join(', ');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRowsForPropertiesWithIncompleteData(
    properties: PropertyWithIncompleteData[],
  ) {
    return properties.map(({ property, periodsWithoutData }) => ({
      [COLUMN_PROPERTIES.NAME]: property.name,
      [COLUMN_PROPERTIES.DATES_WITHOUT_DATA]:
        this.formatDateRanges(periodsWithoutData),
    }));
  }

  @action removePropertiesFromFilter(): void {
    const propertyIds =
      this.args.periodsWithPropertiesWithIncompleteData.flatMap((period) =>
        period.properties.map(({ property }) => property.id),
      );

    // @ts-expect-error: Argument of type '(string | null)[]' is not assignable to parameter of type 'string[]'.
    this.args.onRemovePropertiesFromFilter(propertyIds);
    this.args.onClose();
  }

  <template>
    <PrismModal
      @onClose={{@onClose}}
      @title={{t "total_revenue.properties_with_incomplete_data"}}
    >
      <:default>
        <div class="modal--padded flex flex-col gap-400">
          {{#each this.periodsWithRows as |period|}}
            <span class="heading-3">{{period.title}}</span>
            <PrismTable @columns={{this.columns}} @rows={{period.rows}} />
          {{/each}}
        </div>
      </:default>
      <:footer>
        <div class="flex h-full items-center justify-end gap-300 pr-500 pl-500">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.close"}}
            @onClick={{@onClose}}
          />
          <span>
            <PrismButton
              @isDisabled={{this.isRemoveFiltersDisabled}}
              @label={{t "total_revenue.remove_properties_from_filter"}}
              @onClick={{this.removePropertiesFromFilter}}
            />
            {{#if this.isRemoveFiltersDisabled}}
              <PrismTooltip
                @text={{t
                  "total_revenue.remove_properties_from_filter.disabled.explanation"
                }}
              />
            {{/if}}
          </span>
        </div>
      </:footer>
    </PrismModal>
  </template>
}
